import { useEffect, useRef } from 'react';

/**
 * usePerformanceObserver
 * 
 * Silently monitors the application's framerate (FPS) using requestAnimationFrame.
 * If the user's GPU/CPU starts to struggle (FPS drops below a threshold for a sustained period),
 * this hook automatically applies a CSS class to the body to gracefully disable
 * the most computationally expensive frontend effects (e.g. backdrop-blur).
 */
export const usePerformanceObserver = (
  fpsThreshold = 36, 
  requiredLowFramesSeconds = 3
) => {
  const isEnabled = useRef(false);

  useEffect(() => {
    // Graceful exit if unsupported or SSR
    if (typeof window === 'undefined' || !window.requestAnimationFrame || !window.performance) {
      return;
    }

    let frames = 0;
    let prevTime = performance.now();
    let lowFpsConsecutiveSeconds = 0;
    let rafId;

    const measureFPS = () => {
      const now = performance.now();
      frames++;

      const deltaObj = now - prevTime;

      // Evaluate every second
      if (deltaObj >= 1000) {
        // If delta is abnormally large (e.g., >2000ms), the user probably switched tabs or minimized the browser window.
        // requestAnimationFrame pauses when hidden, so we ignore massive deltas to prevent false positives.
        if (deltaObj < 2000) {
          const fps = Math.round((frames * 1000) / deltaObj);

          if (fps < fpsThreshold) {
            lowFpsConsecutiveSeconds++;
          } else {
            // Recovered, reset the consecutive counter
            lowFpsConsecutiveSeconds = 0;
          }

          // If device has struggled consistently for the threshold duration
          if (lowFpsConsecutiveSeconds >= requiredLowFramesSeconds && !isEnabled.current) {
            console.warn(`[Performance Monitor] Sustained low framerate detected (${fps} FPS). Enabling low-performance UI mode to save GPU resources.`);
            document.body.classList.add('reduce-ui-fx');
            isEnabled.current = true;
            
            // Turn off the monitor loop completely to save battery/resources since we already verified low perf
            return;
          }
        }

        // Reset for the next second rolling window
        prevTime = now;
        frames = 0;
      }

      if (!isEnabled.current) {
        rafId = requestAnimationFrame(measureFPS);
      }
    };

    rafId = requestAnimationFrame(measureFPS);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [fpsThreshold, requiredLowFramesSeconds]);
};

export default usePerformanceObserver;
